// @flow

import moment from 'moment';
import _ from 'lodash';

import DateUtils from '../../utils/DateUtils';

import ApiConstants from '../../constants/ApiConstants';

import ApiObject from '../ApiObject';
import OpeningHours from '../OpeningHours';

import OnSiteOrderSettings from './OnSiteOrderSettings';
import TakeAwayOrderSettings from './TakeAwayOrderSettings';
import DeliveryOrderSettings from './DeliveryOrderSettings';

import DeliveryTimeSettings from './DeliveryTimeSettings';
import OrderNotificationSettings from './OrderNotificationSettings';

import Order from './Order';

export default class OrderSettings extends ApiObject {

  restaurantId: string;

  menuManagedByPos: boolean = false;

  /**
	 * the list of openned periods for which to allow the order
	 */
  openingHours: OpeningHours = new OpeningHours();

  /**
	 * The time needed by the restaurant to prepare some more food.
	 * This should be taken into consideration when ordering food for a specific date
	 */
  manufactureDelay: MsTimestamp;

  /**
	 * Contains the description of all the order settings specific to the On-Site order mode
	 */
  onSiteSettings: OnSiteOrderSettings = new OnSiteOrderSettings();

  /**
   * Contains the description of all the order settings specific to the Take-Away order mode
   */
  takeAwaySettings: TakeAwayOrderSettings = new TakeAwayOrderSettings();

  /**
	 * Contains the description of all the order settings specific to the Delivery order mode
	 */
  deliverySettings: DeliveryOrderSettings = new DeliveryOrderSettings();

  notificationSettings: OrderNotificationSettings = new OrderNotificationSettings();

  postJsonAssignation(data: Object) {
    this.openingHours = new OpeningHours();
    this.openingHours.fromJson(data.openingHours);

    this.onSiteSettings = new OnSiteOrderSettings();
    this.onSiteSettings.fromJson(data.onSiteSettings);

    this.takeAwaySettings = new TakeAwayOrderSettings();
    this.takeAwaySettings.fromJson(data.takeAwaySettings);

    this.deliverySettings = new DeliveryOrderSettings();
    this.deliverySettings.fromJson(data.deliverySettings);

    this.notificationSettings = new OrderNotificationSettings();
    this.notificationSettings.fromJson(data.notificationSettings);
  }

  getForMode(orderMode: number) {
    switch (orderMode) {
      case Order.OrderMode.ON_SITE:
        return this.onSiteSettings;
      case Order.OrderMode.TAKE_AWAY:
        return this.takeAwaySettings;
      case Order.OrderMode.DELIVERY:
        return this.deliverySettings;
      default:
        return null;
    }
  }

  isEnabledForMode(orderMode: number) {
    const settings = this.getForMode(orderMode);

    if (_.isNil(settings)) {
      return false;
    }

    return settings.enabled;
  }

  isOnSiteEnabled(): boolean {
    return this.onSiteSettings.enabled;
  }

  isTakeAwayEnabled(): boolean {
    return this.takeAwaySettings.enabled;
  }

  isDeliveryEnabled(): boolean {
    return this.deliverySettings.enabled;
  }

  getManufactureDelayAsMinutes(): number {
    return DateUtils.millisecondsToMinutes(this.manufactureDelay);
  }

  generateSlotsForDate(date: Moment, delayParam: MsTimestamp = null): DaySlots {
    const isOpen = this.openingHours.isDayOpen(DateUtils.getPmtDayIndex(date));

    let delay = delayParam;
    if (_.isNull(delayParam)) {
      delay = this.manufactureDelay;
    }

    const nowHourTimestamp: number = DateUtils.getTimestampForHourWithDate(moment().add(1, 'minute'));

    if (isOpen) {
      const slots: Array<number> = this.getSlotsForDay(DateUtils.getPmtDayIndex(date));
      let finalSlots = [];

      // is today, remove past slots
      finalSlots = _.filter(slots, (slot) => {
        return (!DateUtils.isToday(date) || slot > (nowHourTimestamp + delay));
      });

      return {
        date: DateUtils.toStartOfDay(date),
        slots: finalSlots
      };
    }

    return {
      date: DateUtils.toStartOfDay(date),
      slots: []
    }
  }

  /**
   * Note: moment.js .day() use sunday as first day.
   * We use monday as first day on the API
   *
   * @param numberOfDays: 0 is just today 2 is today and the 2 following days
   */
  generateSlotsForNextDays(numberOfDays: number, delayParam: MsTimestamp = null): DaySlotsList {
    let date = DateUtils.toStartOfDay(moment());
    const slotsDates = [];

    for (let i = 0; i < numberOfDays + 1; i++) {
      slotsDates.push(this.generateSlotsForDate(moment(date), delayParam));

      // reset to start of day just in case.
      date = moment(date).add(1, 'days');
    }

    // remove days with no slots
    const slots = [];
    _.each(slotsDates, (slotDate) => {
      if (!_.isEmpty(slotDate.slots)) {
        slots.push(slotDate);
      }
    });

    return slots;
  }

  /**
   * Warning: moment.js week start at sunday, not monday
   */
  getSlotsForDay(dayIndex: number): Array<number> {
    return this.openingHours.generateHoursForDay(dayIndex);
  }

  isDateBeforeMinTime(date: Moment): boolean {
    if (!DateUtils.isToday(date.valueOf())) {
      return false;
    }

    const firstSlotHour = this.getNextSlotHour();

    if (firstSlotHour === -1) {
      return true; // TODO: handle error
    }

    const todayTimestamp = DateUtils.toStartOfDay(moment());

    const firstSlot = todayTimestamp + firstSlotHour;

    if (firstSlot > date.valueOf()) {
      return true;
    }

    return false;
  }

  isValidDate(date: Moment, delayParam: MsTimestamp = null): boolean {
    const slotsForDay: Object = this.generateSlotsForDate(date, delayParam);
    const dateSlot: Slot = DateUtils.getSlotFromDate(date);

    let res = false;

    _.each(slotsForDay.slots, (slot: Slot) => {
      if (slot === dateSlot) {
        res = true;
        return false; // quit loop
      }
    });

    return res;
  }

  getNextSlotHour(delayParam: MsTimestamp = null): number {
    const slotsForDays = this.generateSlotsForNextDays(7, delayParam); // in case today is finished.

    if (_.isEmpty(slotsForDays)) {
      return -1;
    }

    const daySlots = slotsForDays[0].slots;

    if (_.isEmpty(daySlots)) {
      return -1;
    }

    const firstSlotHour = daySlots[0];

    return firstSlotHour;
  }

  getNextSlotDate(delayParam: MsTimestamp = null): number {
    const slotsForDays = this.generateSlotsForNextDays(7, delayParam); // in case today is finished.

    if (_.isEmpty(slotsForDays)) {
      return -1;
    }

    const daySlots = slotsForDays[0].slots;

    if (_.isEmpty(daySlots)) {
      return -1;
    }

    const firstSlotHour = daySlots[0];

    const nextSlotDate = moment(slotsForDays[0].date.valueOf() + firstSlotHour);

    return nextSlotDate;
  }

  //
  // Delivery
  //

  /**
   * Returns the delivery price according to the given amount.
   * The delivery can be free when the minimumPriceForFreeDelivery is reached.
   */
  getDeliveryPriceForAmount(amount: Price): Price {
    if (_.isNil(this.deliverySettings)) {
      return 0;
    }

    // if the minimumPriceForFreeDelivery, there is no possibility to have a free delivery
    // if (this.deliverySettings.minimumPriceForFreeDelivery) {
    //   return this.deliverySettings.deliveryPrice;
    // }

    return amount >= this.deliverySettings.minimumPriceForFreeDelivery ?
      0 : this.deliverySettings.deliveryPrice;
  }

  getDeliveryMinimumPriceForFreeDelivery(): Price {
    return this.deliverySettings.minimumPriceForFreeDelivery;
  }

  haveDeliveryMinimumPriceForFreeDelivery(): Price {
    return this.deliverySettings.minimumPriceForFreeDelivery > 0;
  }

  isTooFarToDelivery(distance: Meters): boolean {
    return this.deliverySettings.isTooFarToDelivery(distance);
  }

  //
  // Shortcuts to access AbstractOrderModeSetting data
  //

  getMinimumPriceForMode(mode: OrderMode): Days {
    return this.getForMode(mode).minimumPrice;
  }

  getMaxTimeAheadAsDaysOnMode(mode: OrderMode): Days {
    return this.getForMode(mode).getMaxTimeAheadAsDays();
  }

  getOrderDelay(mode: OrderMode, deliveryDistanceTime: MsTimestamp = DateUtils.MINUTE_AS_MS * 15):MsTimestamp {
    switch (mode) {
      case Order.OrderMode.TAKE_AWAY:
        return this.manufactureDelay;
      case Order.OrderMode.DELIVERY:
        return this.getDeliveryTime(deliveryDistanceTime);
    }
    return null;
  }

  getDeliveryTime(deliveryDistanceTime: MsTimestamp = DateUtils.MINUTE_AS_MS * 15): MsTimestamp {
    switch (this.deliverySettings.deliveryTimeSettings.settingsType) {
      case DeliveryTimeSettings.Type.FIXED:
        return this.manufactureDelay + this.deliverySettings.deliveryTimeSettings.fixTimeSettings.time;

      case DeliveryTimeSettings.Type.ADAPTABLE:
        return this.manufactureDelay
        + this.deliverySettings.deliveryTimeSettings.adaptableTimeSettings.deliveryManArrivalDuration
        + deliveryDistanceTime;

      default:
        return this.deliverySettings.deliveryTimeSettings.fixTimeSettings;
    }

  }


}



// WEBPACK FOOTER //
// ./sdk/scripts/entities/order/OrderSettings.js